/**
 * Модель для работы с настройками прав и ролей
 */

import { createModel } from '@rematch/core';
import { RootModel } from './index';

import { IId, IRoleType, IRole, AccessDecision } from './types';

import UsersService, { IPermission } from '../services/UsersService';
import adminService from '../services/AdminService';
import { parseSecurableTypes } from '../utils/parseSecurableTypes';

interface IData {
  roles: IRoleType[];
  nodeACL: any;
}

interface IList {
  data: IRole[] | null;
  pagesCount: number;
}

interface IState {
  data: IData;
  list: IList;
  listRoles: IRole[] | null;
  secTreeHierarchy: Record<string, unknown>;
  roleItem: IRole | null;
}

const initialState: IState = {
  data: {
    roles: [],
    nodeACL: {}
  },
  list: {
    data: null,
    pagesCount: 0
  },
  listRoles: null,
  secTreeHierarchy: {},
  roleItem: null
};

export const settingSecTree = createModel<RootModel>()({
  state: initialState,
  reducers: {
    setData(state: IState, payload: IData): IState {
      return { ...state, data: { ...state.data, ...payload } };
    },
    setListRoles(state: IState, payload: IRole[]): IState {
      return { ...state, listRoles: payload };
    },
    // setList(state: IState, payload: IList): IState {
    //   return { ...state, list: payload };
    // },
    setHierarchy(state: IState, payload: any): IState {
      return { ...state, secTreeHierarchy: payload };
    },
    setRoleItem(state: IState, payload: IRole): IState {
      return { ...state, roleItem: payload };
    },
    /**
     * Целевое обновление nodeACL после сохранения прав.
     * Работает как reducer — читает текущий state (не устаревший rootState из effect),
     * обновляет только затронутые ветки через shallow-spread, удаляет Deny-ключи.
     * roleKeyed=true: вложенная структура uid → roleKey → action → boolean (режим всех ролей)
     * roleKeyed=false: плоская структура uid → action → boolean (режим одной роли)
     */
    mergePermissionsIntoNodeACL(state: IState, payload: { permissions: IPermission[]; roleKeyed: boolean }): IState {
      const nodeACL = state.data.nodeACL || {};
      const newNodeACL: any = { ...nodeACL };
      const spreadUids = new Set<string>();
      const spreadRoleKeys = new Set<string>();

      for (const perm of payload.permissions) {
        const uid = String(perm.resourceId);
        const isGranted = perm.access === 'Grant';

        if (payload.roleKeyed) {
          const roleKey = `${perm.role}roleId`;

          if (!spreadUids.has(uid)) {
            newNodeACL[uid] = { ...(nodeACL[uid] || {}) };
            spreadUids.add(uid);
          }
          const uidRoleKey = `${uid}|${roleKey}`;
          if (!spreadRoleKeys.has(uidRoleKey)) {
            newNodeACL[uid][roleKey] = { ...(newNodeACL[uid][roleKey] || {}) };
            spreadRoleKeys.add(uidRoleKey);
          }

          if (isGranted) {
            newNodeACL[uid][roleKey][perm.action] = true;
          } else {
            delete newNodeACL[uid][roleKey][perm.action];
          }
        } else {
          if (!spreadUids.has(uid)) {
            newNodeACL[uid] = { ...(nodeACL[uid] || {}) };
            spreadUids.add(uid);
          }

          if (isGranted) {
            newNodeACL[uid][perm.action] = true;
          } else {
            delete newNodeACL[uid][perm.action];
          }
        }
      }

      return { ...state, data: { ...state.data, nodeACL: newNodeACL } };
    },
    reset(): IState {
      return { ...initialState };
    }
  },
  effects: () => ({
    async getData() {
      try {
        const [{ data: roles }] = await Promise.all([UsersService.getRoles()]);
        const mappedRoles = roles.map((role: any) => ({
          name: role.name,
          description: role.description || role.friendlyName || ''
        }));
        this.setData({ roles: mappedRoles });
      } catch (error) {
        return error;
      }
    },
    async getListRoles() {
      try {
        const { data } = await UsersService.getRoles();
        const mappedRoles: IRole[] = data.map((role: any) => ({
          name: role.name,
          description: role.description || role.friendlyName || '',
          actions: {}
        }));
        this.setListRoles(mappedRoles);
      } catch (error) {
        return error;
      }
    },
    // async getList(params, rootState: iRootState) {
    //   try {
    //     const { data } = await ProtectionsService.getTypeAll(params);
    //
    //     this.setList(data);
    //   } catch (error) {
    //     return error;
    //   }
    // },
    async getHierarchy(params: any, rootState?: any) {
      try {
        const { data } = await UsersService.getSecTreeHierarchy();

        const adapt = (n: any): any => {
          if (!n || typeof n !== 'object') return n;
          const children = Array.isArray(n.children) ? n.children.map(adapt) : n.children;
          return {
            ...n,
            uid: n.uid ?? n.id,
            parent: n.parent ?? n.parentId,
            children
          };
        };

        this.setHierarchy(adapt(data));
      } catch (error) {
        return error;
      }
    },
    async getNodeACL(props: any, rootState?: any) {
      try {
        let rolesList = rootState?.settingSecTree?.data?.roles || [];
        if (!rolesList || rolesList.length === 0) {
          const { data: rolesData } = await UsersService.getRoles();
          const mappedRoles = rolesData.map((role: any) => ({
            id: role.id,
            name: role.name,
            description: role.description || role.friendlyName || ''
          }));
          rolesList = mappedRoles;
          this.setData({ roles: mappedRoles });
        }
        // resourceRootId может быть undefined, если props.root не передан или пустая строка
        const resourceRootId = props.root ? String(props.root) : undefined;

        // Передаем role параметр для фильтрации прав на бэкенде
        let roleParam: string | undefined = undefined;
        if (props.roleId) {
          // roleId может быть как числовым ID, так и именем роли (из URL)
          const roleObj = rolesList.find(
            (r: any) => String(r.id) === String(props.roleId) || r.name === String(props.roleId)
          );
          roleParam = roleObj?.name;
        }

        const { data } = await UsersService.getPermissions(roleParam, resourceRootId);

        const getRoleIdByName = (roleName: string) => {
          const found = rolesList.find(
            (r: any) => r.name === roleName || r.description === roleName
          );
          return found?.name;
        };

        const obj: any = {};
        (data as IPermission[]).forEach(item => {
          const uid = String(item.resourceId);
          if (!obj[uid]) {
            obj[uid] = {};
          }
          if (props.roleId) {
            obj[uid][item.action] = item.access === 'Grant';
          } else {
            const roleId = getRoleIdByName(item.role);
            if (roleId == null) return;
            const key = `${roleId}roleId`;
            if (!obj[uid][key]) {
              obj[uid][key] = {};
            }
            obj[uid][key][item.action] = item.access === 'Grant';
          }
        });

        this.setData({
          nodeACL: obj
        });
      } catch (error) {
        return error;
      }
    },
    /**
     * Fetches and parses securable type codes for a tree node.
     * Does not update Redux — Rights/Roles pages store the result locally (see LoadedSecurableTypes)
     * so fast tree navigation cannot render stale columns from a shared store.
     */
    async getSecurableTypes(id: IId) {
      try {
        const { data } = await adminService.getSecurableTypes(id);

        return { securableTypes: parseSecurableTypes(data) };
      } catch (error) {
        return error;
      }
    },
    async getRoleItem(id: IId) {
      try {
        const { data: roles } = await UsersService.getRoles();
        // Сначала ищем по ID, затем по имени (т.к. в URL может передаваться имя роли)
        const role = roles.find((r: any) => String(r.id) === String(id) || r.name === String(id));

        if (role) {
          const mappedRole: IRole = {
            name: role.name,
            description: role.description || role.friendlyName || '',
            actions: {}
          };
          this.setRoleItem(mappedRole);
        }
      } catch (error) {
        return error;
      }
    },

    async updateOrCreate(body: { id?: IId; name?: string; description?: string; actions: Record<string, Record<string, boolean>> }) {
      try {
        const roleBody = {
          id: (body.id ?? 0) as IId,
          name: (body.name ?? '').toString(),
          description: (body.description ?? '').toString()
        };

        await UsersService.updateOrCreateRole(roleBody).then(async ({ data }) => {
          const roleName = roleBody.name;

          const { data: existing } = await UsersService.getPermissions(roleName);
          const existingMap = new Map<string, IId>();
          (existing as IPermission[]).forEach(p => {
            if (p.id != null) existingMap.set(`${String(p.resourceId)}|${p.action}`, p.id as IId);
          });

          const permissions: IPermission[] = Object.entries(body.actions).flatMap(([uid, actions]) => {
            return Object.entries(actions).map(([action, result]) => {
              const key = `${String(uid)}|${action}`;
              const id = existingMap.get(key) ?? null;
              return {
                id,
                resourceId: String(uid),
                role: String(roleName),
                action,
                access: result ? 'Grant' : 'Deny'
              } as IPermission;
            });
          });

          await UsersService.updateOrCreatePermissions(permissions);
        });
      } catch (error) {
        return error;
      }
    },

    async updateRole(body: { id?: IId; name?: string; description?: string }) {
      try {
        const roleBody = {
          id: (body.id ?? 0) as IId,
          name: (body.name ?? '').toString(),
          description: (body.description ?? '').toString()
        };

        await UsersService.updateOrCreateRole(roleBody);
      } catch (error) {
        return error;
      }
    },

    async updateRolePermissions(body: { id?: IId; name?: string; description?: string; actions: Record<string, Record<string, boolean>> }, rootState?: any) {
      try {
        let roleName = (body.name ?? '').toString();

        if (!roleName && body.id) {
          let rolesList = rootState?.settingSecTree?.data?.roles || [];
          if (!rolesList || rolesList.length === 0) {
            const { data: rolesData } = await UsersService.getRoles();
            rolesList = rolesData.map((role: any) => ({
              name: role.name,
              description: role.description || role.friendlyName || ''
            }));
            this.setData({ roles: rolesList });
          }
          const roleObj = rolesList.find((r: any) => String(r.id) === String(body.id) || r.name === String(body.id));
          if (roleObj?.name) roleName = roleObj.name;
        }

        if (!roleName) {
          throw new Error('Role name is required to update permissions');
        }

        // Получаем все права для роли
        const { data: existing } = await UsersService.getPermissions(roleName);
        const existingIdMap = new Map<string, IId>();
        const existingAccessMap = new Map<string, AccessDecision>();
        (existing as IPermission[]).forEach(p => {
          const key = `${String(p.resourceId)}|${p.action}`;
          if (p.id != null) existingIdMap.set(key, p.id as IId);
          if (p.access != null) existingAccessMap.set(key, p.access);
        });

        // Собираем права, которые нужно удалить (те, что есть в existing, но нет в body.actions)
        const permissionsToDelete: IPermission[] = [];
        existingIdMap.forEach((id, key) => {
          const [uid, action] = key.split('|');
          const bodyAction = body.actions[uid]?.[action];
          if (bodyAction === undefined || bodyAction === null) {
            // Это право нужно удалить
            permissionsToDelete.push({
              id,
              resourceId: String(uid),
              role: String(roleName),
              action,
              access: 'Deny'
            } as IPermission);
          }
        });

        // Собираем права, которые нужно добавить или обновить
        const permissionsToUpdate: IPermission[] = [];
        Object.entries(body.actions).forEach(([uid, actions]) => {
          Object.entries(actions).forEach(([action, result]) => {
            const key = `${String(uid)}|${action}`;
            const desired: 'Grant' | 'Deny' = result ? 'Grant' : 'Deny';
            const prev = existingAccessMap.get(key);
            if (prev !== desired) {
              const id = existingIdMap.get(key) ?? null;
              permissionsToUpdate.push({
                id,
                resourceId: String(uid),
                role: String(roleName),
                action,
                access: desired
              } as IPermission);
            }
          });
        });

        const permissions = [...permissionsToDelete, ...permissionsToUpdate];

        if (permissions.length === 0) {
          return;
        }

        await UsersService.updateOrCreatePermissions(permissions);

        this.mergePermissionsIntoNodeACL({ permissions, roleKeyed: false });
      } catch (error) {
        return error;
      }
    },

    async updateOrCreateACL(body: any, rootState?: any) {
      try {
        const { actions } = body;

        const normalizeRoleId = (key: string) => String(key).replace(/roleId$/, '');

        let rolesList = rootState?.settingSecTree?.data?.roles || [];
        if (!rolesList || rolesList.length === 0) {
          const { data: rolesData } = await UsersService.getRoles();
          rolesList = rolesData.map((role: any) => ({
            name: role.name,
            description: role.description || role.friendlyName || ''
          }));
          this.setData({ roles: rolesList });
        }

        const roleIds = new Set<string>([]);
        Object.keys(actions).forEach(uid => {
          Object.keys(actions[uid]).forEach(roleKey => {
            const rid = normalizeRoleId(String(roleKey));
            roleIds.add(rid);
          });
        });

        const roleIdToName = new Map<string, string>();
        roleIds.forEach(rid => {
          const roleObj = rolesList.find((r: any) => String(r.name) === String(rid));
          if (roleObj?.name) roleIdToName.set(String(rid), roleObj.name);
        });

        const existingIdByRole: Record<string, Map<string, IId>> = {};
        const existingAccessByRole: Record<string, Map<string, AccessDecision>> = {};
        const { data: existingAll } = await UsersService.getPermissions();
        (existingAll as IPermission[]).forEach(p => {
          const roleName = String(p.role);
          if (!existingIdByRole[roleName]) {
            existingIdByRole[roleName] = new Map<string, IId>();
          }
          if (!existingAccessByRole[roleName]) {
            existingAccessByRole[roleName] = new Map<string, AccessDecision>();
          }
          const key = `${String(p.resourceId)}|${p.action}`;
          if (p.id != null) {
            existingIdByRole[roleName].set(key, p.id as IId);
          }
          if (p.access != null) {
            existingAccessByRole[roleName].set(key, p.access);
          }
        });

        const resultArray: IPermission[] = [];

        for (const uid of Object.keys(actions)) {
          for (const roleKey of Object.keys(actions[uid])) {
            const rid = normalizeRoleId(String(roleKey));
            const roleName = roleIdToName.get(rid);
            if (!roleName) continue;
            const actionsForRole = actions[uid][roleKey] || {};
            for (const action of Object.keys(actionsForRole)) {
              const key = `${String(uid)}|${action}`;
              const desired: 'Grant' | 'Deny' = actionsForRole[action] ? 'Grant' : 'Deny';
              const prev = existingAccessByRole[String(roleName)]?.get(key);
              if (prev !== desired) {
                const id = existingIdByRole[String(roleName)]?.get(key) ?? null;
                resultArray.push({
                  id,
                  resourceId: String(uid),
                  role: String(roleName),
                  action,
                  access: desired
                });
              }
            }
          }
        }

        if (resultArray.length === 0) {
          // Нечего обновлять — не отправляем пустой массив, чтобы не затирать права
          return;
        }
        await UsersService.updateOrCreatePermissions(resultArray);

        this.mergePermissionsIntoNodeACL({ permissions: resultArray, roleKeyed: true });
      } catch (error) {
        return error;
      }
    },

    async remove(name: string) {
      try {
        await UsersService.deleteRole(name);
      } catch (error) {
        return error;
      }
    }
  })
});
